import React, { memo, useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useT, useCtx } from "@al/next/al-ex";

import { tracker } from "~/common/tracker";

import Stars from "../../components/Stars";
import SectionTitle from "../../components/SectionTitle";
import { updateState, TabKey } from "../../components/FixedTab/utils";
import EvaluationItem from "../../../common/components/EvaluationItem";
import { EvaluationItemType, IEvaluation } from "../../../common/components/EvaluationItem/types";
import BigImg from "../../../common/components/BigImg";
import { transformData } from "../../../common/components/BigImg/utils";
import redux from "../../redux";
import { getTrackerSaReportInfo } from "../../../sensor-config";

import styles from "./styles.scss";

type Props = {
  spuId: string;
};

const Evaluate: React.FC<Props> = (props) => {
  const t = useT();
  const ctx = useCtx();
  const dispatch = ctx.store.dispatch;
  const { spuId } = props;
  const [detail, setDetail] = useState<{
    score: string;
    foldedCount: number;
    spuCommentInfos: IEvaluation[];
  }>({
    score: "0",
    foldedCount: 0,
    spuCommentInfos: [],
  });

  const [bigImgVisible, setBigImgVisible] = useState(false);
  const [bigImgRecord, setBigImgRecord] = useState({} as any);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (process.browser && wrapperRef.current) {
      updateState({ name: TabKey.Evaluate, el: wrapperRef.current });
    }
  }, [wrapperRef.current]);

  useEffect(() => {
    async function getDetailEvaluate(spuId: string): Promise<void> {
      const { data, success } = await dispatch(redux.actions.asyncGetDetailEvaluate({ spuId }));

      if (success && data) {
        const score = data.score ? (Math.ceil(Number(data.score * 10)) / 10).toFixed(1) : null;
        setDetail({ ...data, score });
        dispatch(redux.actions.setSpuScore(score));
      }
    }
    spuId && getDetailEvaluate(spuId);
  }, [spuId]);

  const handleCloseBigImg = useCallback(() => setBigImgVisible(false), []);

  const onMoreClick = useCallback(() => {
    tracker.click({
      sa: getTrackerSaReportInfo({ page: "homePage", eventName: "clickViewAllPreview" }),
    });
    ctx.router.push(`/product/evaluation?spuId=${spuId}`);
  }, [spuId]);

  // 点击图片 查看大图
  const handleClickImg = useCallback((commentId: number, index: number): void => {
    setBigImgRecord({ commentId, index });
    tracker.click({
      sa: getTrackerSaReportInfo({ page: "homePage", eventName: "clickViewPic" }),
    });
    setBigImgVisible(true);
  }, []);

  const isHasEvaluation = useMemo(() => detail.spuCommentInfos?.length, [detail.spuCommentInfos]);
  const isFolderEvaluation = useMemo(() => detail.foldedCount > 0, [detail.foldedCount]);

  const dataSource = useMemo(() => transformData(detail.spuCommentInfos), [detail.spuCommentInfos]);

  return (
    <div ref={wrapperRef} className={styles.box}>
      <SectionTitle title={t("评价")} rightClick={isHasEvaluation > 1 ? onMoreClick : undefined} />
      {/* 评分 */}
      {isHasEvaluation || isFolderEvaluation ? (
        <>
          <div className={styles.score_box}>
            <span className={styles.score}>{detail.score || "0"}</span>
            <Stars className={styles.evaluation_rate} value={detail.score} />
          </div>

          {!isHasEvaluation && isFolderEvaluation ? (
            <div className={styles.folder_text}>
              {t("{{x}}条评价已折叠", { x: detail.foldedCount > 999 ? "999+" : detail.foldedCount })}
            </div>
          ) : (
            <>
              {/* 评论列表 */}
              {detail.spuCommentInfos?.map((item, i) => (
                <EvaluationItem
                  wrapClassName={styles.evaluation_item}
                  type={EvaluationItemType.DetailModule}
                  key={item?.commentInfo?.commentId + i}
                  detail={item}
                  clickImg={handleClickImg}
                />
              ))}

              {/* 预览大图 */}
              <BigImg
                defaultCommentId={bigImgRecord?.commentId}
                defaultIndex={bigImgRecord?.index}
                dataSource={dataSource}
                closeModal={handleCloseBigImg}
                visible={bigImgVisible}
              />
            </>
          )}
        </>
      ) : (
        <div className={styles.no_records}>{t("暂时没有评价")}</div>
      )}
    </div>
  );
};

export default memo(Evaluate);
